// Canonical shapes for the cross-device video state sync feature.
// This module has no runtime deps so it can be imported safely from server,
// web client, and iPhone native runtime without dragging in Prisma, React,
// or browser-only globals.

export interface ProgressRow {
  videoId: string;
  watchedSeconds: number;
  clientUpdatedAt: string;
}

export interface BookmarkRow {
  videoId: string;
  bookmarked: boolean;
  clientUpdatedAt: string;
}

export interface ClipBookmarkRow {
  videoId: string;
  transcriptId: string;
  artifactType: "PROPOSAL_EXTRACTS" | "RECOMMENDER_EXTRACTS";
  clipKey: string;
  title: string;
  startSeconds: number;
  endSeconds: number;
  // Proposal or recommender the clip was saved from (the rail is implied by
  // artifactType). Used to deep-link back to the originating clips view.
  sourceContextId: string;
  bookmarked: boolean;
  clientUpdatedAt: string;
}

// Stable composite identity for a saved clip. Mirrors the hidden-clip key
// scheme: a clip is uniquely a (transcript, artifact type, timestamp range).
export function clipBookmarkKey(row: {
  transcriptId: string;
  artifactType: string;
  clipKey: string;
}): string {
  return `${row.transcriptId}|${row.artifactType}|${row.clipKey}`;
}

export interface PlaylistRow {
  videoId: string;
  position: number;
  clientUpdatedAt: string;
}

export interface GetUserVideoStateResponse {
  progress: ProgressRow[];
  bookmarks: BookmarkRow[];
  clipBookmarks: ClipBookmarkRow[];
  playlist: PlaylistRow[];
  // Per-user sentinel: the clientUpdatedAt of the last playlist mutation.
  // Needed to LWW the playlist even when it's empty (a cleared playlist has
  // no entry rows to carry a timestamp). Null when the user has never
  // mutated their playlist.
  playlistClientUpdatedAt: string | null;
}

// Cadence for client → server watch-progress writes. Used by both the
// iPhone WatchProgressThrottle and the web VideoStatesProvider's lodash
// throttle so behaviour is consistent across platforms.
export const watchProgressThrottleMs = 30_000;
